import { ApolloClient } from '@apollo/client'
import {
  GetCentreStoreDirectoryDocument,
  GetCentreStoreDirectoryQuery,
  SgCentreStoresSearchOrder,
} from '@scentregroup/shared/graphql'
import {
  StoreDetails,
  CentreStoreDirectory,
} from '@scentregroup/shared/types/browse-stores'

export const PAGE_LIMIT = 24

type Query = {
  centreSlug: string
  page: number
  retailCategoryIds: string[] | null
  sortBy: SgCentreStoresSearchOrder
}

export async function getStoresForCentreV2<TCacheShape>(
  apollo: Pick<ApolloClient<TCacheShape>, 'query'>,
  { centreSlug, page, retailCategoryIds, sortBy }: Query
): Promise<CentreStoreDirectory> {
  const limit = PAGE_LIMIT
  const skip = page * limit
  const { data } = await apollo.query({
    context: { version: 2 },
    query: GetCentreStoreDirectoryDocument,
    variables: {
      limit,
      skip,
      filter: {
        centreSlug,
        retailCategoryIds,
      },
      order: sortBy,
    },
  })

  return {
    total: data?.sgCentreStoresSearch?.total || 0,
    items: transform(data?.sgCentreStoresSearch).filter(
      (store): store is StoreDetails => Boolean(store)
    ),
  }
}

export function transform(
  data?: GetCentreStoreDirectoryQuery['sgCentreStoresSearch']
): StoreDetails[] {
  if (!data?.items) {
    return []
  }

  return data.items.flatMap(store => {
    const {
      title,
      slug,
      paymentMethodsFromStoreSearch,
      retailer,
      legacyIdentifier,
      logoUrl,
      longDescription,
      centreSlug,
      landmarkDescription,
      categoriesFromStoresSearch,
    } = store

    const { spaceId, id: storeId } = store.sys

    if (
      !spaceId ||
      !storeId ||
      !title ||
      !slug ||
      !paymentMethodsFromStoreSearch ||
      !centreSlug
    ) {
      return []
    }

    const categories =
      categoriesFromStoresSearch?.map(cat => ({
        id: cat.sys.id,
        level: cat.level ?? null,
        parentId: cat.parentCategory?.sys.id || null,
        seoContentSchema: cat.seoContentSchema ?? null,
        seoTitle: cat.seoTitle || '',
        shortDescription: cat.shortDescription || '',
        title: cat.title,
        __typename: cat.__typename,
      })) || []

    return {
      _type: 'STORE',
      spaceId,
      id: storeId,
      title,
      slug,
      logoUrl,
      centreSlug,
      longDescription,
      landmarkDescription,
      legacyIdentifier,
      paymentMethods: paymentMethodsFromStoreSearch,
      categories,
      retailer: {
        paymentMethods:
          retailer.paymentMethods?.flatMap(pay => pay ?? []) || [],
      },
    }
  })
}
